import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useKeyAction } from "hooks/useKeyAction";
import { useLatest } from "hooks/useLatest";
import { isFormControlFocused } from "utils/isFormControlFocused/isFormControlFocused";
import { hasOpenModal } from "components/common/Modal/modalStack";
import { useNodeDragging } from "../useRoutesCreationModeOrchestration/useNodeDragging";
import {
  MIN_POLYGON_POINTS,
  insertPointOnSegment,
  removePoint,
  setPointPosition,
} from "../../utils/polygonPointMutators";
import type { NodeDraggingState } from "../useRoutesCreationModeOrchestration/useNodeDragging/types";
import type {
  InsertNodeOnEdgeArgs,
  InsertNodeOnEdgeResult,
  SetNodePositionArgs,
} from "../../RoutesCreationModeV3/hooks/types";
import type { PolygonPoint } from "../../utils/polygonPointMutators";
import type {
  EditedPolygonGeometry,
  PolygonEditorModal,
  SavedPolygon,
  SelectedPolygonTarget,
  UsePolygonEditorArgs,
  UsePolygonEditorResult,
} from "./types";

export const DRAFT_POLYGON_ID = "draft";
const MAX_POLYGON_POINTS = 1000;
const DOUBLE_CLICK_THRESHOLD_MS = 300;

export const usePolygonEditor = ({
  isActive,
  step,
  savedPolygons,
  setStep,
  stop,
  persistGeometry,
  maxPointsMessage,
  minPointsMessage,
  fixedTargetId,
  onDraftFinalized,
}: UsePolygonEditorArgs): UsePolygonEditorResult => {
  const lastClickTimestampRef = useRef(0);
  const pendingGeometryRef = useRef(false);

  const [draftPoints, setDraftPoints] = useState<PolygonPoint[] | null>(null);
  const [selectedTarget, setSelectedTarget] =
    useState<SelectedPolygonTarget | null>(null);
  // null = no unsaved drag edits; rendering falls through to the saved points.
  const [editedGeometry, setEditedGeometry] =
    useState<EditedPolygonGeometry | null>(null);
  const [selectedPointId, setSelectedPointId] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<PolygonPoint | null>(null);
  const [modal, setModal] = useState<PolygonEditorModal | null>(null);

  const selectedPolygon = useMemo(() => {
    if (selectedTarget?.type !== "saved") return null;
    const polygon = savedPolygons.find(
      (candidate) => candidate.id === selectedTarget.id,
    );
    return polygon ?? null;
  }, [savedPolygons, selectedTarget]);

  const draftPointsLatest = useLatest(draftPoints);
  const selectedTargetLatest = useLatest(selectedTarget);
  const editedGeometryLatest = useLatest(editedGeometry);
  const savedPointsLatest = useLatest(selectedPolygon?.points ?? null);

  // Drawing: user is laying down the points of a new polygon.
  // Editing: user is mutating the points of the selected polygon (drag/insert).
  const isDrawingActive = isActive && step === "drawing";
  const isEditingActive =
    isActive && step === "list" && selectedTarget !== null;

  // The only path into "list" with a live draft is a successful finalize.
  const isDraftFinalized = draftPoints !== null && step === "list";

  const targetPoints = useMemo(() => {
    if (selectedTarget === null) return null;
    if (selectedTarget.type === "draft") return draftPoints;
    if (editedGeometry !== null && editedGeometry.id === selectedTarget.id) {
      return editedGeometry.points;
    }
    return selectedPolygon?.points ?? null;
  }, [selectedTarget, draftPoints, editedGeometry, selectedPolygon]);

  const persistPoints = useCallback(
    async (id: number, points: PolygonPoint[]) => {
      await persistGeometry({ id, points });
      // Only clear the exact geometry this PATCH carried: on success the cache now
      // holds it, on failure clearing reverts to the cache — and either way edits
      // made while the request was in flight survive untouched.
      setEditedGeometry((previous) =>
        previous !== null && previous.points === points ? null : previous,
      );
    },
    [persistGeometry],
  );

  const applyPointsToTarget = useCallback(
    (updater: (points: PolygonPoint[]) => PolygonPoint[]) => {
      const target = selectedTargetLatest.current;
      if (target === null) return;
      if (target.type === "draft") {
        setDraftPoints((previous) => (previous ? updater(previous) : previous));
        return;
      }
      pendingGeometryRef.current = true;
      setEditedGeometry((previous) => {
        const base =
          previous !== null && previous.id === target.id
            ? previous.points
            : savedPointsLatest.current;
        if (!base) return previous;
        return { id: target.id, points: updater(base) };
      });
    },
    [selectedTargetLatest, savedPointsLatest],
  );

  const getTargetPointsSnapshot = useCallback(() => {
    const target = selectedTargetLatest.current;
    if (target === null) return null;
    if (target.type === "draft") return draftPointsLatest.current;
    const geometry = editedGeometryLatest.current;
    if (geometry !== null && geometry.id === target.id) {
      return geometry.points;
    }
    return savedPointsLatest.current;
  }, [
    selectedTargetLatest,
    draftPointsLatest,
    editedGeometryLatest,
    savedPointsLatest,
  ]);

  const setNodePositionForDragging = useCallback(
    ({ routeIndex, nodeId, pos }: SetNodePositionArgs) => {
      if (routeIndex !== 0) return;
      applyPointsToTarget((points) =>
        setPointPosition({ points, index: nodeId - 1, position: pos }),
      );
    },
    [applyPointsToTarget],
  );

  const insertNodeOnEdgeForDragging = useCallback(
    ({
      routeIndex,
      edgeIndex,
      pos,
    }: InsertNodeOnEdgeArgs): InsertNodeOnEdgeResult | null => {
      if (routeIndex !== 0) return null;
      const points = getTargetPointsSnapshot();
      if (!points) return null;
      const inserted = insertPointOnSegment({
        points,
        segmentIndex: edgeIndex,
        position: pos,
      });
      applyPointsToTarget(() => inserted.points);
      // Point ids are positional (index + 1), so the insert shifts every id at or
      // after it by one; move the selection along so it keeps marking the same point.
      setSelectedPointId((previous) =>
        previous !== null && previous > inserted.newIndex
          ? previous + 1
          : previous,
      );
      return { newNodeId: inserted.newIndex + 1 };
    },
    [getTargetPointsSnapshot, applyPointsToTarget],
  );

  const draggingState = useMemo<NodeDraggingState>(() => {
    const pseudoRouteId =
      selectedTarget?.type === "saved"
        ? String(selectedTarget.id)
        : DRAFT_POLYGON_ID;
    return {
      routes: targetPoints
        ? [
            {
              id: pseudoRouteId,
              nodes: targetPoints.map((point, index) => ({
                id: index + 1,
                x: point.x,
                y: point.y,
              })),
            },
          ]
        : [],
      hoverPosition,
      insertNodeOnEdge: insertNodeOnEdgeForDragging,
      setNodePosition: setNodePositionForDragging,
      setSelectedNodeId: setSelectedPointId,
    };
  }, [
    selectedTarget,
    targetPoints,
    hoverPosition,
    insertNodeOnEdgeForDragging,
    setNodePositionForDragging,
  ]);

  const {
    handleNodeMouseDown,
    handleSegmentMouseDown,
    applyDragPosition,
    endDrag,
  } = useNodeDragging({
    isEditingActive,
    creationModeState: draggingState,
  });

  const startDrawing = useCallback(() => {
    setDraftPoints([]);
    setSelectedTarget(null);
    setSelectedPointId(null);
    setStep("drawing");
  }, [setStep]);

  const discardDraft = useCallback(() => {
    setDraftPoints(null);
    setHoverPosition(null);
    setStep("list");
  }, [setStep]);

  const finalizeDraft = useCallback(() => {
    const points = draftPointsLatest.current;
    // Too few points is not a mistake worth reporting — there is simply no polygon to keep.
    if (points === null || points.length < MIN_POLYGON_POINTS) {
      discardDraft();
      return;
    }
    if (onDraftFinalized) {
      const id = onDraftFinalized(points);
      discardDraft();
      setSelectedTarget({ type: "saved", id });
      setSelectedPointId(null);
      return;
    }
    setSelectedTarget({ type: "draft" });
    setStep("list");
    setHoverPosition(null);
  }, [draftPointsLatest, discardDraft, setStep, onDraftFinalized]);

  const cancelDrawing = useCallback(() => {
    const points = draftPointsLatest.current;
    // Only an already-valid polygon is worth a confirmation prompt.
    if (points !== null && points.length >= MIN_POLYGON_POINTS) {
      setModal({ type: "discardDraft", intent: "draftOnly" });
      return;
    }
    discardDraft();
  }, [draftPointsLatest, discardDraft]);

  const handleMapClick = useCallback(
    (pos: PolygonPoint) => {
      if (!isDrawingActive) return;

      const now = Date.now();
      const isDoubleClick =
        now - lastClickTimestampRef.current < DOUBLE_CLICK_THRESHOLD_MS;
      lastClickTimestampRef.current = now;

      if (isDoubleClick) {
        finalizeDraft();
        return;
      }

      setDraftPoints((previous) => {
        const points = previous ?? [];
        if (points.length >= MAX_POLYGON_POINTS) {
          toast.error(maxPointsMessage);
          return points;
        }
        return [...points, pos];
      });
    },
    [isDrawingActive, finalizeDraft, maxPointsMessage],
  );

  const handleMapMouseMove = useCallback(
    (pos: PolygonPoint | null) => {
      if (pos && applyDragPosition(pos)) return;
      setHoverPosition(pos);
    },
    [applyDragPosition],
  );

  const deleteSelectedPoint = useCallback(() => {
    const target = selectedTargetLatest.current;
    if (target === null || selectedPointId === null) return;

    const points = getTargetPointsSnapshot();
    if (!points) return;

    const updated = removePoint({ points, index: selectedPointId - 1 });
    if (!updated) {
      toast.error(minPointsMessage);
      return;
    }

    // Point ids are positional, so a drag still in progress would move the next point.
    endDrag();
    // The direct persist below replaces the mouseup flush; the flag would send the same points again.
    pendingGeometryRef.current = false;
    setSelectedPointId(null);
    if (target.type === "draft") {
      setDraftPoints(updated);
      return;
    }
    setEditedGeometry({ id: target.id, points: updated });
    // Keyboard deletes have no mouseup to flush on, so persist directly.
    void persistPoints(target.id, updated);
  }, [
    selectedTargetLatest,
    selectedPointId,
    getTargetPointsSnapshot,
    endDrag,
    persistPoints,
    minPointsMessage,
  ]);

  const requestDeletePolygon = useCallback((id: number) => {
    setModal({ type: "deletePolygon", id });
  }, []);

  const onPolygonDeleted = useCallback((id: number) => {
    setSelectedTarget((current) =>
      current?.type === "saved" && current.id === id ? null : current,
    );
  }, []);

  const onDraftSaved = useCallback((id: number) => {
    setDraftPoints(null);
    setSelectedTarget({ type: "saved", id });
  }, []);

  const selectTarget = useCallback(
    (targetId: string) => {
      if (fixedTargetId !== undefined) return;
      setSelectedPointId(null);
      if (targetId === DRAFT_POLYGON_ID) {
        setSelectedTarget((current) =>
          current?.type === "draft" ? null : { type: "draft" },
        );
        return;
      }
      const parsedId = parseInt(targetId, 10);
      setSelectedTarget((current) =>
        current?.type === "saved" && current.id === parsedId
          ? null
          : { type: "saved", id: parsedId },
      );
    },
    [fixedTargetId],
  );

  const requestDiscardDraft = useCallback(() => {
    setModal({ type: "discardDraft", intent: "draftOnly" });
  }, []);

  const requestClose = useCallback(() => {
    const points = draftPointsLatest.current;
    // An incomplete sketch is dropped silently, same as Escape while drawing.
    if (points !== null && points.length >= MIN_POLYGON_POINTS) {
      setModal({ type: "discardDraft", intent: "close" });
      return;
    }
    stop();
  }, [draftPointsLatest, stop]);

  const confirmDiscard = useCallback(() => {
    if (modal?.type !== "discardDraft") return;
    setDraftPoints(null);
    setSelectedTarget((current) =>
      current?.type === "draft" ? null : current,
    );
    if (modal.intent === "close") {
      stop();
    } else if (step === "drawing") {
      setStep("list");
    }
    setModal(null);
  }, [modal, step, stop, setStep]);

  const closeModal = useCallback(() => {
    setModal(null);
  }, []);

  const resolvePoints = useCallback(
    (polygon: SavedPolygon) =>
      editedGeometry !== null && editedGeometry.id === polygon.id
        ? editedGeometry.points
        : polygon.points,
    [editedGeometry],
  );

  const finalizeFromKeyboard = useCallback(
    (event: KeyboardEvent) => {
      if (isFormControlFocused(event)) return;
      event.preventDefault();
      finalizeDraft();
    },
    [finalizeDraft],
  );

  const cancelDrawingFromKeyboard = useCallback(
    (event: KeyboardEvent) => {
      if (isFormControlFocused(event)) return;
      event.preventDefault();
      cancelDrawing();
    },
    [cancelDrawing],
  );

  const isDrawingKeyActionDisabled = step !== "drawing" || modal !== null;

  useKeyAction({
    code: "Enter",
    callback: finalizeFromKeyboard,
    options: { isDisabled: isDrawingKeyActionDisabled },
  });

  useKeyAction({
    code: "Escape",
    callback: cancelDrawingFromKeyboard,
    options: { isDisabled: isDrawingKeyActionDisabled },
  });

  const deletePointFromKeyboard = useCallback(
    (event: KeyboardEvent) => {
      // The room delete modal is the inspector's own, not this editor's; the key is its then.
      if (isFormControlFocused(event) || hasOpenModal()) return;
      deleteSelectedPoint();
    },
    [deleteSelectedPoint],
  );

  const isDeletePointDisabled =
    step !== "list" ||
    selectedPointId === null ||
    selectedTarget === null ||
    modal !== null;

  useKeyAction({
    code: "KeyD",
    callback: deletePointFromKeyboard,
    options: { isDisabled: isDeletePointDisabled },
  });

  useKeyAction({
    code: "Backspace",
    callback: deletePointFromKeyboard,
    options: { isDisabled: isDeletePointDisabled },
  });

  useKeyAction({
    code: "Delete",
    callback: deletePointFromKeyboard,
    options: { isDisabled: isDeletePointDisabled },
  });

  // Flush the PATCH once the drag that touched geometry ends.
  useEffect(() => {
    const handleDocumentMouseUp = () => {
      if (!pendingGeometryRef.current) return;
      pendingGeometryRef.current = false;
      const target = selectedTargetLatest.current;
      const geometry = editedGeometryLatest.current;
      if (
        target === null ||
        target.type !== "saved" ||
        geometry === null ||
        geometry.id !== target.id
      ) {
        return;
      }
      void persistPoints(target.id, geometry.points);
    };
    document.addEventListener("mouseup", handleDocumentMouseUp);
    return () => document.removeEventListener("mouseup", handleDocumentMouseUp);
  }, [selectedTargetLatest, editedGeometryLatest, persistPoints]);

  // Reset all local state whenever the editor closes (incl. resetToIdle paths); a pinned polygon starts selected.
  useEffect(() => {
    if (isActive) {
      if (fixedTargetId !== undefined) {
        setSelectedTarget({ type: "saved", id: fixedTargetId });
        setSelectedPointId(null);
      }
      return;
    }
    setDraftPoints(null);
    setSelectedTarget(null);
    setEditedGeometry(null);
    setSelectedPointId(null);
    setHoverPosition(null);
    setModal(null);
    pendingGeometryRef.current = false;
  }, [isActive, fixedTargetId]);

  return {
    draftPoints,
    selectedTarget,
    selectedPointId,
    hoverPosition,
    modal,
    isDrawingActive,
    isEditingActive,
    isDraftFinalized,
    resolvePoints,
    selectTarget,
    startDrawing,
    finalizeDraft,
    requestDiscardDraft,
    requestClose,
    confirmDiscard,
    closeModal,
    requestDeletePolygon,
    onPolygonDeleted,
    onDraftSaved,
    handleMapClick,
    handleMapMouseMove,
    handleNodeMouseDown,
    handleSegmentMouseDown,
  };
};
